import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, CheckCircle2, Gavel, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import logoMark from "@/assets/logo-mark.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERROR_MESSAGES = {
  token_invalido: "Link de confirmação inválido. Peça um novo cadastro.",
  token_expirado: "Link de confirmação expirado. Peça um novo cadastro.",
  convite_necessario: "Para criar uma conta nova é preciso um código de convite. Digite-o no campo abaixo e tente de novo.",
  convite_invalido: "Código de convite inválido, expirado ou já usado. Confira com quem te enviou.",
};

export default function Login() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [convite, setConvite] = useState("");

  useEffect(() => {
    const error = searchParams.get("error");
    if (error) {
      toast.error(ERROR_MESSAGES[error] || "Não foi possível concluir. Tente novamente.");
      searchParams.delete("error");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleLogin = () => {
    const q = convite.trim() ? `?convite=${encodeURIComponent(convite.trim())}` : "";
    window.location.href = `${BACKEND_URL}/api/auth/google/login${q}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await http.post("/auth/login", { email, password });
      await checkAuth();
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "E-mail ou senha inválidos");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-stone-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative z-10 flex items-center gap-2">
          <img src={logoMark} alt="" className="w-9 h-9 rounded-md" />
          <span className="font-display text-xl font-bold tracking-tight"><span className="text-gold-400">IA</span>pauta</span>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-gold-400 mb-3 font-bold">
              Automação Jurídica
            </p>
            <h1 className="font-display text-5xl font-bold leading-[1.05] mb-4">
              Nunca mais perca<br />uma audiência.
            </h1>
            <p className="text-stone-300 text-base leading-relaxed max-w-md">
              Buscamos as publicações do DJEN todo dia, extraímos as audiências com IA
              e já colocamos no seu Google Calendar automaticamente.
            </p>
          </div>

          <div className="space-y-3 text-sm">
            {[
              "Consulta automática ao DJEN por OAB",
              "Extração de data, hora e vara por IA",
              "Agendamento automático no seu calendário",
              "WhatsApp + E-mail para o cliente",
            ].map((t) => (
              <div key={t} className="flex items-center gap-3">
                <CheckCircle2 className="w-4 h-4 text-gold-400 shrink-0" strokeWidth={2} />
                <span className="text-stone-200">{t}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-stone-500">
          © 2026 IApauta — feito para advogados brasileiros.
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 sm:px-12 py-16">
        <div className="w-full max-w-sm mx-auto space-y-8">
          <div className="lg:hidden flex items-center gap-2">
            <img src={logoMark} alt="" className="w-9 h-9 rounded-md" />
            <span className="font-display text-xl font-bold"><span className="text-gold-600">IA</span>pauta</span>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/15 border border-gold-500/25 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-emerald-brand" strokeWidth={2} />
              <span className="text-xs font-semibold text-gold-700 tracking-wide">Beta privado</span>
            </div>
            <h2 className="font-display text-3xl font-bold text-stone-900 mb-2">
              Entrar na sua conta
            </h2>
            <p className="text-sm text-stone-500 leading-relaxed">
              Use sua conta Google para vincular automaticamente ao seu calendário,
              ou entre com e-mail e senha.
            </p>
          </div>

          <Tabs defaultValue={searchParams.get("tab") === "senha" ? "password" : "google"} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger data-testid="tab-google" value="google">Google</TabsTrigger>
              <TabsTrigger data-testid="tab-password" value="password">E-mail e senha</TabsTrigger>
            </TabsList>

            <TabsContent value="google" className="pt-5">
              <div className="mb-4 space-y-1.5">
                <Label htmlFor="google-convite" className="text-xs text-stone-500">
                  Primeiro acesso? Código de convite
                </Label>
                <Input
                  id="google-convite"
                  data-testid="google-convite-input"
                  value={convite}
                  onChange={(e) => setConvite(e.target.value)}
                  placeholder="Só para quem ainda não tem conta"
                  autoComplete="off"
                />
              </div>
              <Button
                data-testid="google-login-button"
                onClick={handleGoogleLogin}
                className="w-full h-12 bg-stone-950 hover:bg-stone-800 text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" opacity=".7"/>
                  <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity=".85"/>
                  <path fill="#fff" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" opacity=".55"/>
                  <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                </svg>
                Continuar com Google
              </Button>
              <p className="text-xs text-stone-500 mt-3 leading-relaxed">
                Suas audiências ficam vinculadas ao seu Google Calendar automaticamente.
              </p>
            </TabsContent>

            <TabsContent value="password" className="pt-5">
              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email">E-mail</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="voce@escritorio.com.br"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Senha</Label>
                    <Link
                      to="/esqueci-senha"
                      state={{ email }}
                      data-testid="esqueci-senha-link"
                      className="text-xs text-stone-500 hover:text-stone-800 underline underline-offset-2"
                    >
                      Esqueci minha senha
                    </Link>
                  </div>
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  data-testid="password-login-button"
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-emerald-brand text-white font-medium rounded-md"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
                </Button>
              </form>
              <p className="text-xs text-stone-500 mt-3">
                Ainda não tem conta?{" "}
                <Link to="/cadastro" className="text-emerald-brand font-medium hover:underline">
                  Criar conta com convite
                </Link>
              </p>
            </TabsContent>
          </Tabs>

          <div className="pt-4 border-t border-stone-200">
            <div className="flex items-start gap-3 text-xs text-stone-500 leading-relaxed">
              <Gavel className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
              <p>
                Ao continuar, você concorda com o{" "}
                <Link to="/termo" target="_blank" data-testid="login-termo-link" className="underline hover:text-stone-700">Termo de Uso e Aviso de Privacidade</Link>. Audiências identificadas
                pela IA geralmente são agendadas automaticamente — você deve conferir e validar.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
